import React, { useEffect, useRef, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import L, { Map as LeafletMap } from 'leaflet';
import { Route } from '../types/route.types';
import { getActivityColor } from '../utils/mapUtils';
import { activityLabels } from '../constants/activities';
import { RouteTooltip, ActivityKey } from '../components/routes/RouteTooltip';

/**
 * Hook pour gérer les marqueurs de la carte
 */
export const useMapMarkers = (
  mapInstance: React.RefObject<LeafletMap | null>,
  allRoutes: Route[],
  filteredRoutes: Route[],
  selectedActivity?: ActivityKey
) => {
  const currentLayer = useRef<L.LayerGroup | null>(null);
  const markersMap = useRef<Map<number, L.CircleMarker>>(new Map());

  const getActivityLabel = useCallback((activity: string) => {
    return activityLabels[activity] || activity;
  }, []);

  // Créer tous les marqueurs
  const createAllMarkers = useCallback((routes: Route[]): void => {
    if (!mapInstance.current) return;

    const canvasRenderer = L.canvas({ padding: 0.5 });
    const group = L.layerGroup();

    markersMap.current.clear();

    routes.forEach(route => {
      if (route.longitude && route.latitude) {
        const lat = route.latitude;
        const lng = route.longitude;
        const color = getActivityColor(route.activities[0]);

        const marker = L.circleMarker([lat, lng], {
          renderer: canvasRenderer,
          radius: 4,
          color: '#fff',
          weight: 1.5,
          fillColor: color,
          fillOpacity: 1,
          opacity: 1
        });

        // Gestionnaire de clic pour ouvrir Camptocamp
        marker.on('click', () => {
          const camptocampUrl = `https://www.camptocamp.org/routes/${route.documentId}`;
          const link = document.createElement('a');
          link.href = camptocampUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          const event = new MouseEvent('click', {
            ctrlKey: true,
            metaKey: true,
            bubbles: true,
            cancelable: true
          });
          link.dispatchEvent(event);
        });

        // Tooltip enrichi
        const tooltipTitle = route.titlePrefix && route.title
          ? `${route.titlePrefix} - ${route.title}`
          : route.title || 'Route sans nom';

        const getRatings = (route: Route) => {
          const ratings = [];
          if (route.hikingRating) ratings.push(route.hikingRating);
          if (route.globalRating) ratings.push(route.globalRating);

          const mainActivity = route.activities[0];
          if (mainActivity === 'rock_climbing') {
            if (route.rockFreeRating && route.rockRequiredRating) {
              ratings.push(`${route.rockFreeRating}/${route.rockRequiredRating}`);
            } else if (route.rockFreeRating) {
              ratings.push(route.rockFreeRating);
            } else if (route.rockRequiredRating) {
              ratings.push(route.rockRequiredRating);
            }
          }

          if (route.engagementRating) ratings.push(route.engagementRating);
          if (route.riskRating) ratings.push(route.riskRating);
          if (route.equipmentRating) ratings.push(route.equipmentRating);
          if (route.expositionRockRating) ratings.push(route.expositionRockRating);
          if (route.aidRating) ratings.push(route.aidRating);
          if (route.hikingMtbExposition) ratings.push(route.hikingMtbExposition);

          return ratings;
        };

        const ratings = getRatings(route);
        const activities = route.activities.map(getActivityLabel).join(', ');

        const tooltipContent = renderToStaticMarkup(
          <RouteTooltip
            route={route}
            tooltipTitle={tooltipTitle}
            activities={activities}
            ratings={ratings}
            selectedActivity={selectedActivity}
          />
        );

        marker.bindTooltip(tooltipContent, {
          permanent: false,
          direction: 'top',
          offset: [0, -10],
          className: 'custom-tooltip',
          opacity: 1
        });

        markersMap.current.set(route.id, marker);
        group.addLayer(marker);
      }
    });

    if (currentLayer.current) {
      mapInstance.current.removeLayer(currentLayer.current);
    }

    currentLayer.current = group;
    mapInstance.current.addLayer(group);
  }, [mapInstance, getActivityLabel, selectedActivity]);

  // Filtrer les marqueurs existants
  const filterExistingMarkers = useCallback((filteredRoutes: Route[]): void => {
    if (!currentLayer.current) return;

    const filteredIds = new Set(filteredRoutes.map(route => route.id));

    markersMap.current.forEach((marker, routeId) => {
      if (filteredIds.has(routeId)) {
        if (!currentLayer.current!.hasLayer(marker)) {
          currentLayer.current!.addLayer(marker);
        }
      } else {
        if (currentLayer.current!.hasLayer(marker)) {
          currentLayer.current!.removeLayer(marker);
        }
      }
    });
  }, []);

  // Créer les marqueurs quand allRoutes change
  useEffect(() => {
    if (!mapInstance.current || !allRoutes.length) return;

    mapInstance.current.whenReady(() => {
      if (!mapInstance.current) return;
      createAllMarkers(allRoutes);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRoutes, createAllMarkers, mapInstance]);

  // Filtrer les marqueurs quand filteredRoutes change
  useEffect(() => {
    if (!mapInstance.current || !currentLayer.current) return;
    filterExistingMarkers(filteredRoutes);
  }, [filteredRoutes, filterExistingMarkers, mapInstance]);

  return {
    currentLayer,
    markersMap,
  };
};
